import { useState, useRef, useCallback } from 'react';
import type { ChangeEvent } from 'react';
import type { AttachSessionKind } from '../lib/attachSession';
import {
  filterFilesForSession,
  isCrossKindAttachSession,
  partitionValidFileSizes,
  resolveIncomingAttachFiles,
} from '../lib/attachSession';
import { assertWhatsAppFileSize } from './whatsappMediaLimits';
import { notify } from '../../../utils/toast';

export type AttachSpecialMode = 'contact' | 'pix' | 'location';

export interface StagedAttachItem {
  id: string;
  file: File;
  previewUrl: string;
}

interface UseChatAttachFlowOptions {
  onSendMedia: (file: File, caption?: string) => void | Promise<void>;
  /** IG Direct: paste/drop só foto/vídeo (igual ao menu +). */
  mediaOnly?: boolean;
}

let stagedIdCounter = 0;

function createStagedId(): string {
  stagedIdCounter += 1;
  return `attach-${Date.now()}-${stagedIdCounter}`;
}

function toFileArray(files: FileList | File[]): File[] {
  return Array.from(files);
}

export function useChatAttachFlow({ onSendMedia, mediaOnly = false }: UseChatAttachFlowOptions) {
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [sessionKind, setSessionKind] = useState<AttachSessionKind | null>(null);
  const [staged, setStaged] = useState<StagedAttachItem[]>([]);
  const [caption, setCaption] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [specialMode, setSpecialMode] = useState<AttachSpecialMode | null>(null);

  const revokeStaged = useCallback((items: StagedAttachItem[]) => {
    for (const item of items) {
      URL.revokeObjectURL(item.previewUrl);
    }
  }, []);

  const closePreview = useCallback(() => {
    setStaged((prev) => {
      revokeStaged(prev);
      return [];
    });
    setCaption('');
    setSessionKind(null);
    setIsPreviewOpen(false);
  }, [revokeStaged]);

  const appendStagedFiles = useCallback((files: File[], kind: AttachSessionKind) => {
    const items = files.map((file) => ({
      id: createStagedId(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setSessionKind(kind);
    setStaged((prev) => [...prev, ...items]);
    setIsPreviewOpen(true);
  }, []);

  const onFilesSelected = useCallback(
    (files: FileList | File[], kind: AttachSessionKind) => {
      const fileArray = toFileArray(files);
      if (fileArray.length === 0) return;

      if (isCrossKindAttachSession(sessionKind, kind, staged.length > 0)) {
        notify.info(
          'Não é possível misturar fotos/vídeos e documentos no mesmo envio. Envie ou remova os anexos atuais primeiro.'
        );
        return;
      }

      const { accepted, rejected } = filterFilesForSession(fileArray, kind);
      if (rejected.length > 0) {
        notify.info(
          'Alguns arquivos foram ignorados por não corresponderem a este tipo de envio.'
        );
      }

      const { valid, invalid } = partitionValidFileSizes(accepted, assertWhatsAppFileSize);
      for (const { message } of invalid) {
        notify.info(message);
      }

      if (valid.length > 0) {
        appendStagedFiles(valid, kind);
      }
    },
    [appendStagedFiles, sessionKind, staged.length]
  );

  /** Paste e drop — mesma entrada; IG usa mediaOnly (igual ao menu +). */
  const stageIncomingFiles = useCallback(
    (files: File[]) => {
      const resolved = resolveIncomingAttachFiles(files, { mediaOnly });
      if (!resolved || resolved.accepted.length === 0) {
        if (mediaOnly && files.length > 0) {
          notify.info('No Instagram Direct só é possível enviar fotos e vídeos.');
        }
        return;
      }
      onFilesSelected(resolved.accepted, resolved.kind);
    },
    [mediaOnly, onFilesSelected],
  );

  const removeStaged = useCallback((id: string) => {
    let shouldClearSession = false;
    setStaged((prev) => {
      const item = prev.find((entry) => entry.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      const next = prev.filter((entry) => entry.id !== id);
      shouldClearSession = next.length === 0;
      return next;
    });
    if (shouldClearSession) {
      setSessionKind(null);
      setIsPreviewOpen(false);
      setCaption('');
    }
  }, []);

  const confirmSend = useCallback(async () => {
    const files = staged.map((item) => item.file);
    const cap = caption.trim();
    closePreview();
    for (let i = 0; i < files.length; i++) {
      try {
        await onSendMedia(files[i], cap || undefined);
      } catch {
        // toast already in sendMedia; continue
      }
      if (i < files.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }
  }, [staged, caption, closePreview, onSendMedia]);

  const openMediaPicker = useCallback(() => {
    mediaInputRef.current?.click();
  }, []);

  const openDocumentPicker = useCallback(() => {
    documentInputRef.current?.click();
  }, []);

    const openSpecial = useCallback((mode: AttachSpecialMode) => {
        setSpecialMode(mode);
    }, []);

    const closeSpecial = useCallback(() => {
        setSpecialMode(null);
    }, []);

  const addMoreFiles = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || !sessionKind) return;
      onFilesSelected(files, sessionKind);
      e.target.value = '';
    },
    [sessionKind, onFilesSelected]
  );

  return {
    sessionKind,
    staged,
    caption,
    setCaption,
    isPreviewOpen,
    specialMode,
    mediaInputRef,
    documentInputRef,
        openMediaPicker,
        openDocumentPicker,
        openSpecial,
        closeSpecial,
        onFilesSelected,
    addMoreFiles,
    removeStaged,
    closePreview,
    confirmSend,
    stageIncomingFiles,
    /** @deprecated alias — use stageIncomingFiles (paste + drop). */
    stageFromPaste: stageIncomingFiles,
  };
}
